<template>
  <main class="main main--messaging main--full-height">
    <div
      class="appointments messaging group group--first-last group--wide messaging--sidebar"
    >
      <img class="appointments__image" :src="illustration" >
      <div class="appointments__content">
        <h1 
          :style="{ 'color': org.custom_color1 == null ? '': org.custom_color1 }"
          class="c-accent appointments__title">My Appointments</h1>

        <div v-if="userAppointments.length <= 0">
          <p class="mt2 p xlarge fw-regular">
            You don't have any upcoming phone or video sessions.
          </p>
          <p class="pt2 p fw-regular">
            Looking for next steps?
            <router-link to="/appointment-type">Get started</router-link> with a
            request for emotional support<br >
            or give us a call at {{ setPhone(org.phone) }}.
          </p>
        </div>

        <div class="appointments__header" v-if="userAppointments.length > 0">
          <h3>Counselor</h3>
          <h3>Details</h3>
          <h3>Status</h3>
        </div>

        <div class="appointments__table" v-if="userAppointments.length > 0">
          <div
            v-for="appointment in userAppointments"
            :key="appointment.id"
            class="table"
          >
            <template v-if="cancelling === 'confirm'">
              <div
                class="cover cover--fixed bg-primary ovh df aic jcc appointments__overlay"
                key="confirm"
              >
                <div>
                  <h1 
                    :style="{ 'color': org.custom_color1 == null ? '': org.custom_color1 }"
                    class="c-accent">Are you sure you want to cancel?</h1>
                  <p class="p">
                    You might not be able to get another appointment at the same
                    time or with the same counselor.
                  </p>
                  <module-button
                    @click.native="cancelAppointment(appointmentId)"
                    button="secondary"
                  >
                    Yes, Cancel
                  </module-button>
                  <module-button
                    @click.native="
                      () => {
                        cancelling = false;
                      }
                    "
                    button="secondary"
                  >
                    No, never mind
                  </module-button>
                </div>
              </div>
            </template>

            <div class="table__column">
              <h3 
                :style="{ 'color': org.custom_color1 == null ? '': org.custom_color1 }"
                class="c-accent h2 mt0 mb0">
                {{ appointment.provider.full_name }}
              </h3>
              <p class="p pt0 mt0 fw-bold mb0">
                {{ appointment.provider.provider_type }}
              </p>
            </div>

            <div class="table__column mr2">
              <p
                :style="{ 'color': org.custom_color1 == null ? '': org.custom_color1 }"
                :class="[
                  `p mt0 mb0 df aic`,
                  { 'c-accent': isAfter(appointment.start_date) }
                ]"
              >
                <img
                  v-if="
                    appointment.appointment_by === 'video' &&
                      isAfter(appointment.start_date)
                  "
                  :src="images['videoAccent'].src"
                  :alt="images['videoAccent'].alt"
                  width="16px"
                  class="mr-tiny"
                >
                <img
                  v-if="
                    appointment.appointment_by === 'phone' &&
                      isAfter(appointment.start_date)
                  "
                  :src="images['phoneAccent'].src"
                  :alt="images['phoneAccent'].alt"
                  width="16px"
                  class="mr-tiny"
                >
                <img
                  v-if="!isAfter(appointment.start_date)"
                  :src="images[appointment.appointment_by].src"
                  :alt="images[appointment.appointment_by].alt"
                  width="16px"
                  class="mr-tiny"
                >
                {{ formatDate(appointment.start_date) }}
              </p>
            </div>

            <div class="table__column">
              <module-button
                v-if="
                  isSoon(appointment.start_date) &&
                    appointment.appointment_by === 'video' && !appointment.completed
                "
                size="s"
                @click.native="openAppointment(appointment.id)"
                button="primary"
              >
                Join Video Chat
              </module-button>
              <module-button
                v-if="
                  isAfter(appointment.start_date) &&
                    !isSoon(appointment.start_date)
                "
                size="s"
                @click.native="rescheduleAppointment(appointment)"
              >
                Reschedule
              </module-button>
              <module-button
                v-if="
                  isAfter(appointment.start_date) &&
                    !isSoon(appointment.start_date)
                "
                size="s"
                @click.native="
                  () => {
                    cancelling = 'confirm';
                    appointmentId = appointment.id;
                  }
                "
              >
                Cancel
              </module-button>

              <p v-if="appointment.completed">Completed</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <ui-module-loader
      key="loader"
      @messages-complete="onMessagesComplete"
      v-if="cancelling === 'loading'"
      :messages="[
        'Please give us a moment to cancel',
        'All set, your appointment has been cancelled'
      ]"
    />
  </main>
</template>

<script>
import moment from 'moment';
import formatPhone from 'utils/phone';
import { mapGetters, mapActions } from 'vuex';

import video from 'images/icons/icon-video.svg';
import videoAccent from 'images/icons/icon-video-accent.svg';
import phone from 'images/icons/icon-phone.svg';
import phoneAccent from 'images/icons/icon-phone-accent.svg';

import UiModuleLoader from 'components/ModuleLoader';

import appointmentImage from 'assets/images/illustrations/register-success-illustration.png';

export default {
  name: 'view-appointments',

  components: {
    UiModuleLoader,
  },

  data() {
    return {
      cancelling: false,
      setPhone: formatPhone,
      appointmentId: null,
      illustration: appointmentImage,
      images: {
        video: {
          src: video,
          alt: 'Video Call',
        },
        phone: {
          src: phone,
          alt: 'Phone Call',
        },
        videoAccent: {
          src: videoAccent,
          alt: 'Phone Call',
        },
        phoneAccent: {
          src: phoneAccent,
          alt: 'Phone Call',
        },
      },
    };
  },

  computed: {
    ...mapGetters('appointment', ['appointments', 'patient']),
    ...mapGetters('app', ['org']),

    userAppointments() {
      const completedAppts = [];
      // eslint-disable-next-line array-callback-return
      this.patient.map(appt => {
        completedAppts.push({
          id: appt.id,
          provider: {
            id: appt.provider_id,
            full_name: appt.physician_name,
            provider_type: appt.provider_type,
          },
          start_date: appt.start_time,
          appointment_by: appt.consultation,
          completed: true,
        });
      });

      return this.appointments.concat(completedAppts);
    },
  },

  methods: {
    ...mapActions('appointment', [
      'requestVideoUrl',
      'requestCancelAppointment',
    ]),
    ...mapActions('provider', ['saveSelectedProvider']),
    ...mapActions('appointment', ['requestRetrievePatient']),

    rescheduleAppointment(appt) {
      localStorage.setItem('rescheduling', true);
      localStorage.setItem('rescheduleId', appt.id);
      this.saveSelectedProvider(appt.provider);
      this.$router.push({
        name: 'provider-schedule',
      });
    },

    onMessagesComplete() {
      this.cancelling = false;
      window.location.reload();
    },

    cancelAppointment(id) {
      this.cancelling = 'loading';
      const patient = JSON.parse(localStorage.getItem('updateUser'));
      const model = {
        id,
        patient: patient.mdlive_id,
      };

      localStorage.removeItem('rescheduling');
      localStorage.removeItem('rescheduleId');
      this.requestCancelAppointment(model);
    },

    openAppointment(id) {
      const win = window.open();
      this.requestVideoUrl(id).then(res => {
        win.location = res.url;
        win.focus();
      });
    },

    formatDate(time) {
      const getTime = moment(time);
      const today = moment();
      const tomorrow = moment().add(1, 'day');
      const yesterday = moment().subtract(1, 'day');

      if (getTime.isSame(today, 'day')) {
        return `Today, ${getTime.format('h:mma')}`;
      }

      if (getTime.isSame(tomorrow, 'day')) {
        return `Tomorrow, ${getTime.format('h:mma')}`;
      }

      if (getTime.isSame(yesterday, 'day')) {
        return `Yesterday, ${getTime.format('h:mma')}`;
      }

      return getTime.format('MMM Do, h:mma');
    },

    isAfter(time) {
      const today = moment(new Date().toISOString()).format();
      return moment(time).isSameOrAfter(today);
    },

    isSoon(time) {
      const now = moment(new Date().toISOString()).format();

      return moment(time).diff(moment(now), 'minutes') <= 5 && moment(time).diff(moment(now), 'minutes') >= -60;
    },
  },
};
</script>



// WEBPACK FOOTER //
// Appointments.vue?38df327e